'use client';

import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  budgetLabel,
  categoryLabel,
  fetchBuyerRequests,
  postedAgo,
  respondToBuyerRequest,
  visibleDetails,
  type BuyerRequest,
} from '@/lib/buyerRequests';
import { startConversationWithMessage } from '@/lib/messaging';
import RevealContactPanel from '@/components/RevealContactPanel';

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'cattle', label: 'Cattle' },
  { value: 'sheep', label: 'Sheep' },
  { value: 'pigs', label: 'Pigs' },
  { value: 'horses', label: 'Horses & Ponies' },
  { value: 'goats', label: 'Goats' },
  { value: 'poultry', label: 'Poultry' },
  { value: 'deer', label: 'Deer' },
  { value: 'alpacas', label: 'Alpacas & Llamas' },
  { value: 'machinery', label: 'Machinery' },
  { value: 'deadstock', label: 'Deadstock / By-products' },
  { value: 'other', label: 'Other' },
];

const PAGE_SIZE = 24;

function RequestCard({ request }: { request: BuyerRequest }) {
  const supabase = useMemo(() => createClient(), []);
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const details = visibleDetails(request);
  const isOwn = user?.id === request.buyer_id;

  const send = async () => {
    if (!message.trim()) {
      setError('Please write a short message so the buyer knows what you have.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const body = message.trim();
      // Record the response against the request, then open a real thread with
      // the buyer so it becomes a conversation rather than a one-way note.
      await respondToBuyerRequest(supabase, request.id, body);
      try {
        const id = await startConversationWithMessage(supabase, {
          otherMemberId: request.buyer_id,
          buyerRequestId: request.id,
          subject: `Wanted: ${request.title}`,
          body,
        });
        setConversationId(id);
      } catch {
        // The response is already recorded; don't imply it failed.
      }
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your response.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-primary bg-primary/10 rounded px-2 py-0.5">
            <Icon name="MagnifyingGlassIcon" size={11} />
            Wanted
          </span>
          <h3 className="font-bold text-foreground mt-2 leading-snug">{request.title}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {[categoryLabel(request.category), request.breed, request.county]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-primary whitespace-nowrap">{budgetLabel(request)}</p>
          {request.quantity != null && (
            <p className="text-xs text-muted-foreground mt-0.5">Qty {request.quantity}</p>
          )}
        </div>
      </div>

      {request.description && (
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
          {request.description}
        </p>
      )}

      {details.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs border-t border-border pt-3">
          {details.slice(0, 6).map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="text-foreground font-medium truncate">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-border pt-3 mt-auto">
        <span className="text-xs text-muted-foreground">{postedAgo(request.created_at)}</span>

        {isOwn ? (
          <span className="text-xs font-semibold text-muted-foreground">Your request</span>
        ) : sent ? (
          <Link
            href={conversationId ? `/messaging-centre?c=${conversationId}` : '/messaging-centre'}
            className="text-xs font-bold text-green-700 flex items-center gap-1 hover:underline"
          >
            <Icon name="CheckCircleIcon" size={14} />
            Sent — open conversation
          </Link>
        ) : user ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="btn-primary text-xs py-2 px-4"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={14} />
            {open ? 'Cancel' : 'I have this'}
          </button>
        ) : (
          <Link href="/sign-up-login-screen" className="btn-secondary text-xs py-2 px-4">
            Sign in to respond
          </Link>
        )}
      </div>

      {/* A seller can reveal the buyer's number here — rarer than the other
          direction, but the same allowance applies. */}
      {!isOwn && (
        <RevealContactPanel
          buyerRequestId={request.id}
          ownerName="This buyer"
          compact
        />
      )}

      {open && !sent && (
        <div className="border-t border-border pt-3">
          <label className="text-xs font-semibold text-foreground">
            Tell the buyer what you have
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="form-input mt-1.5 text-sm"
            placeholder="e.g. I have 15 Limousin cross heifers, 20 months, Red Tractor assured, available from next week."
          />
          {error && <p className="text-xs text-red-700 mt-1.5">{error}</p>}
          <p className="text-[11px] text-muted-foreground mt-1.5">
            This opens a conversation with the buyer. They see your name and county, and can reply
            in their messaging centre.
          </p>
          <button
            type="button"
            onClick={send}
            disabled={sending}
            className="btn-primary text-xs py-2 px-4 mt-2 disabled:opacity-60"
          >
            <Icon name="PaperAirplaneIcon" size={14} />
            {sending ? 'Sending…' : 'Send response'}
          </button>
        </div>
      )}
    </div>
  );
}

function BuyerRequests() {
  const supabase = useMemo(() => createClient(), []);
  const searchParams = useSearchParams();

  const [requests, setRequests] = useState<BuyerRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [category, setCategory] = useState(searchParams?.get('cat') ?? '');
  const [q, setQ] = useState(searchParams?.get('q') ?? '');
  const [search, setSearch] = useState(searchParams?.get('q') ?? '');
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchBuyerRequests(supabase, {
        category: category || undefined,
        q: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRequests(res.requests);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load buyer requests.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, category, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <main className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-foreground">Wanted</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            What buyers are looking for right now. If you have it, respond directly — no
            commission, no middleman. Buyers&apos; contact details stay private until they reply
            to you.
          </p>
        </div>
        <Link href="/post-listing?type=wanted" className="btn-primary self-start sm:self-auto">
          <Icon name="PlusCircleIcon" size={18} />
          Post what you want
        </Link>
      </div>

      {/* Filters */}
      <div className="card flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <label className="sr-only" htmlFor="br-search">
            Search buyer requests
          </label>
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="br-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(0);
                  setSearch(q);
                }
              }}
              placeholder="Breed, type, keyword…"
              className="form-input pl-9"
            />
          </div>
        </div>
        <select
          value={category}
          onChange={(e) => {
            setPage(0);
            setCategory(e.target.value);
          }}
          className="form-input sm:w-56"
          aria-label="Filter by category"
        >
          {CATEGORIES.map((c) => (
            <option key={c.value || 'all'} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            setPage(0);
            setSearch(q);
          }}
          className="btn-secondary sm:w-auto"
        >
          Search
        </button>
      </div>

      {error && (
        <div
          className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-5"
          role="alert"
        >
          <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <p className="text-sm text-muted-foreground mb-4">
        {loading ? 'Loading…' : `${total} open request${total === 1 ? '' : 's'}`}
      </p>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} className="card h-52 animate-pulse bg-muted/50" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <Icon name="ClipboardDocumentListIcon" size={40} className="text-muted-foreground mb-3" />
          <h2 className="text-lg font-bold text-foreground mb-1">No open requests yet</h2>
          <p className="text-muted-foreground text-sm max-w-md">
            {category || search
              ? 'Nothing matches that search. Try a different category or keyword.'
              : 'Buyer requests appear here once they have been reviewed. Be the first to post what you are looking for.'}
          </p>
          <Link href="/post-listing?type=wanted" className="btn-primary mt-5">
            <Icon name="PlusCircleIcon" size={18} />
            Post what you want
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {requests.map((r) => (
            <RequestCard key={r.id} request={r} />
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="btn-secondary text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground px-2">
            Page {page + 1} of {pages}
          </span>
          <button
            type="button"
            disabled={page + 1 >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </main>
  );
}

// useSearchParams needs a Suspense boundary so the page keeps its static shell.
export default function BuyerRequestsScreen() {
  return (
    <Suspense
      fallback={
        <main className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
          <div className="h-8 w-48 bg-muted rounded animate-pulse mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`s-${i}`} className="card h-52 animate-pulse bg-muted/50" />
            ))}
          </div>
        </main>
      }
    >
      <BuyerRequests />
    </Suspense>
  );
}
